// Pure filter-state helpers for the Advanced Selection question filter. Kept free of
// React / styled-components / design tokens so non-panel consumers (LessonsStandards,
// ViewChangeLesson, tests) can import them without pulling in the whole UI chain.

// The default filter state. Auto-assign-early-finishers defaults ON; standards /
// learning components / question type stay unfiltered. This default-on state is still treated as "no
// filter" by the backend and dropped on assign — only an explicit opt-OUT (or any
// other narrowing) counts as a real customization. See isNoOpFilter.
export const EMPTY_QUESTION_FILTER = {
  standards: [],
  learningComponents: [],
  questionType: "both",
  autoAssignEarlyFinishers: true,
};

// True when the filter is a no-op (nothing narrowed) — used to decide whether to send
// it on assign. Since auto-assign-early-finishers now defaults to true, only an
// explicit opt-out (false) counts as a real customization — true (the default) does not.
export const isNoOpFilter = (f) =>
  (!f?.standards?.length) &&
  (!f?.learningComponents?.length) &&
  (!f?.questionType || f.questionType === "both") &&
  f?.autoAssignEarlyFinishers !== false;

// Inverse of serializeQuestionFilter: hydrate a stored snake_case `question_filter`
// (as returned on an assignment) back into the UI's camelCase filter state. Returns
// null when there's nothing stored.
export const fromQuestionFilter = (qf) => {
  if (!qf) return null;
  return {
    standards: qf.standards || [],
    learningComponents: qf.learning_components || [],
    questionType: qf.question_type || "both",
    autoAssignEarlyFinishers: Boolean(qf.auto_assign_early_finishers),
  };
};

// Serialize one QuestionFilterState to the backend's snake_case `question_filter`
// shape, or null when it's a no-op (nothing to send).
export const serializeQuestionFilter = (f) => {
  if (isNoOpFilter(f)) return null;
  const out = {};
  if (f.standards?.length) out.standards = f.standards;
  if (f.learningComponents?.length) out.learning_components = f.learningComponents;
  if (f.questionType && f.questionType !== "both") out.question_type = f.questionType;
  if (f.autoAssignEarlyFinishers) out.auto_assign_early_finishers = true;
  return out;
};

// Collect a `${trackType}|${standardCode}` → QuestionFilterState map into the assign
// contract's TrackQuestionFilter[]. No-op entries are dropped. The backend matches each
// created group by (track_type, standard_code); unmatched entries are ignored server-side.
export const toTrackQuestionFilters = (filters = {}) =>
  Object.entries(filters)
    .map(([key, f]) => {
      const question_filter = serializeQuestionFilter(f);
      if (!question_filter) return null;
      const sep = key.indexOf("|");
      const trackType = sep >= 0 ? key.slice(0, sep) : key;
      const standardCode = sep >= 0 ? key.slice(sep + 1) : "";
      return { track_type: trackType, standard_code: standardCode || null, question_filter };
    })
    .filter(Boolean);

// The set of track types ("core" | "prerequisites" | "extensions") that carry at
// least one applied (non-no-op) filter — drives the "Customized" tag in the Assign
// Groups modal.
export const customizedTrackTypes = (filters = {}) =>
  new Set(toTrackQuestionFilters(filters).map((t) => t.track_type));
